import "react-native-gesture-handler";
import "expo-dev-client";
import "./i18n";
import "./intercom-override.css";

import { ActionSheetProvider } from "@expo/react-native-action-sheet";
import {
  DMSans_400Regular as dmSans400Regular,
  DMSans_500Medium as dmSans500Medium,
  DMSans_700Bold as dmSans700Bold,
} from "@expo-google-fonts/dm-sans";
import {
  InstrumentSerif_400Regular as instrumentSerif400Regular,
  InstrumentSerif_400Regular_Italic as instrumentSerif400RegularItalic,
} from "@expo-google-fonts/instrument-serif";
import {
  Inter_400Regular as inter400Regular,
  Inter_500Medium as inter500Medium,
  Inter_600SemiBold as inter600SemiBold,
  Inter_700Bold as inter700Bold,
  useFonts,
} from "@expo-google-fonts/inter";
import {
  PlayfairDisplay_400Regular as playfairDisplay400Regular,
  PlayfairDisplay_700Bold as playfairDisplay700Bold,
} from "@expo-google-fonts/playfair-display";
import {
  Poppins_400Regular as poppins400Regular,
  Poppins_500Medium as poppins500Medium,
  Poppins_600SemiBold as poppins600SemiBold,
  Poppins_700Bold as poppins700Bold,
} from "@expo-google-fonts/poppins";
// NOTE: Adding @sentry/react to the dependencies causes an error because of dependencies it installs.
// Our other sentry dependencies (@sentry/react-native) already install @sentry/react so we can ignore this error.
// eslint-disable-next-line import/no-extraneous-dependencies
import * as SentryReact from "@sentry/react";
import Constants from "expo-constants";
import { StatusBar } from "expo-status-bar";
import Updates from "expo-updates";
import { FlagsProvider } from "flagged";
import _ from "lodash";
import { Button, Center, NativeBaseProvider, Text, View } from "native-base";
import platform from "platform-detect";
import React, { Dispatch } from "react";
import SSRProvider from "react-bootstrap/SSRProvider";
import { TFunction, useTranslation } from "react-i18next";
import { Dimensions, LogBox, StyleSheet } from "react-native";
import { Theme, ThemeProvider } from "react-native-elements";
import { SafeAreaView } from "react-native-safe-area-context";
import { enableFreeze } from "react-native-screens";
import { Provider, useDispatch, useSelector } from "react-redux";
import { persistStore } from "redux-persist";
import { PersistGate } from "redux-persist/integration/react";
import * as Sentry from "sentry-expo";

import AppLoadingScreen from "./app/src/components/AppLoadingScreen";
import { PRODUCTION, SENTRY_DSN } from "./app/src/constants";
import { getTheme } from "./app/src/constants/theme";
import { trackStripeCheckoutPurchaseEvent } from "./app/src/helpers/facebookPixelHelpers";
import { createFeaturesObject, FeatureFlag, isFeatureFlagActive } from "./app/src/helpers/featureFlags";
import { addOpacityToBaseHexColour } from "./app/src/helpers/generalHelpers";
import { logout } from "./app/src/helpers/logout";
import NavigationStack from "./app/src/navigation/NavigationStack";
import logger from "./app/src/services/logger";
import { getBrandSelector } from "./app/src/slices/brandSlice";
import { onboardingDataSelector } from "./app/src/slices/onboardingSlice";
import { userSelector } from "./app/src/slices/userSlice";
import { store } from "./store";

enableFreeze(true);

console.log("Loading real Segment snippet because we are on a web platform");

// Configure Intercom to hide default launcher before it loads through Segment
// This prevents the launcher from appearing briefly before our hide command runs
if (typeof window !== "undefined") {
  window.intercomSettings = {
    hide_default_launcher: true,
    alignment: "left",
    horizontal_padding: 20,
    vertical_padding: 60,
  };
}

require("./app/src/snippets/segment_snippet");
require("./app/src/snippets/facebook_pixel_snippet");

const LOG_MESSAGES_TO_IGNORE = ["SerializableStateInvariantMiddleware", "ImmutableStateInvariantMiddleware"];

if (!PRODUCTION) {
  LogBox.ignoreLogs(LOG_MESSAGES_TO_IGNORE);
}

if (SENTRY_DSN) {
  console.log("Sentry enabled.");

  Sentry.init({
    dsn: SENTRY_DSN,
    enableInExpoDevelopment: true,
    debug: !PRODUCTION,
    // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
    // We recommend adjusting this value in production.
    tracesSampleRate: 0.05,
    beforeSend(event, hint) {
      if (hint) {
        const exception = hint.originalException;
        const errorMessage = exception instanceof Error ? exception.message : exception;
        const MIDDLEWARE_EXCEPTION_NAME = "api/executeQuery/rejected";
        if (errorMessage && _.startsWith(errorMessage, MIDDLEWARE_EXCEPTION_NAME)) {
          // NOTE: We do this to avoid Sentry grouping different errors which are captured in the middleware

          // eslint-disable-next-line no-param-reassign
          event.fingerprint = [errorMessage];
        }
      }

      return event;
    },
  });
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#fff",
  },
  webContainer: {
    flex: 1,
    backgroundColor: "transparent", // Allow content backgrounds to show through
  },
});

function App(): JSX.Element | null {
  const [loaded] = useFonts({
    DMSans_400Regular: dmSans400Regular,
    DMSans_500Medium: dmSans500Medium,
    DMSans_700Bold: dmSans700Bold,
    Inter_400Regular: inter400Regular,
    Inter_500Medium: inter500Medium,
    Inter_600SemiBold: inter600SemiBold,
    Inter_700Bold: inter700Bold,
    PlayfairDisplay_400Regular: playfairDisplay400Regular,
    PlayfairDisplay_700Bold: playfairDisplay700Bold,
    Poppins_400Regular: poppins400Regular,
    Poppins_500Medium: poppins500Medium,
    Poppins_600SemiBold: poppins600SemiBold,
    Poppins_700Bold: poppins700Bold,
    InstrumentSerif_400Regular: instrumentSerif400Regular,
    InstrumentSerif_400Regular_Italic: instrumentSerif400RegularItalic,
  });

  // Add useEffect to verify Facebook Pixel is loaded and fire purchase event
  React.useEffect((): (() => void) | void => trackStripeCheckoutPurchaseEvent(document.referrer), []);

  // Handle font loading completion - remove HTML loader when fonts are ready
  React.useEffect(() => {
    if (loaded) {
      // Remove the HTML loader if it still exists
      const loader = document.getElementById("initial-loader");
      if (loader) {
        loader.style.display = "none";
      }
    }
  }, [loaded]);

  // Don't block render on fonts - let the app load with fallback fonts
  // if (!loaded) {
  //   return null;
  // }

  // TODO: Consider using this: https://www.npmjs.com/package/@bugsnag/plugin-react-native-unhandled-rejection
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function globalPromiseRejectionHandler(this: WindowEventHandlers, p: PromiseRejectionEvent): any {
    console.log("Unhandled Rejection at: Promise", p);
    console.log("promise", JSON.stringify(p, null, 2));

    console.log("Reason: ", this);
  }

  // Here we assign our handler to the corresponding global, window property
  window.onunhandledrejection = globalPromiseRejectionHandler;

  const ErrorBoundaryFallbackComponent = ({
    error,
    resetError,
    t,
    dispatch,
  }: {
    error: Error;
    resetError: () => void;
    t: TFunction;
    dispatch: Dispatch<unknown>;
  }): JSX.Element => (
    <View
      style={{
        flex: 1,
        alignItems: "center",
      }}
    >
      <View mb="4">
        <Center>
          <Text mt="10">{t("general.error_boundary_generic_error")}</Text>
        </Center>
        {error ? <Text>{String(error)}</Text> : null}
      </View>

      <Button onPress={resetError}>{t("general.error_boundary_try_again_button_text")}</Button>
      <Button
        onPress={() => {
          logout(dispatch);
          resetError();
        }}
        mt="2"
      >
        {t("general.error_boundary_reset_app_button")}
      </Button>
    </View>
  );

  const NativeBaseApp = (): JSX.Element => {
    const user = useSelector(userSelector);
    const brand = useSelector(getBrandSelector);

    const dispatch = useDispatch();
    const { t } = useTranslation();

    const onboardingData = useSelector(onboardingDataSelector);

    const nativeBaseTheme = getTheme(user, brand, onboardingData);

    // Helper function to detect if device has home indicator
    const hasHomeIndicator = (): boolean => {
      // Only iOS devices can have home indicator
      if (!platform.ios) return false;

      // Check if running in a device with home indicator
      // This detects iPhone X and newer models
      const { height, width } = Dimensions.get("window");
      const deviceRatio = height / width;

      // iPhone X, XS, 11 Pro, 12 mini, 13 mini have ratio ~2.17
      // iPhone XR, XS Max, 11, 11 Pro Max, 12/13/14 have higher ratios
      // Earlier iPhones have lower ratios
      return deviceRatio > 2.0;
    };

    /**
     * This is to account for the home indicator on iOS (native only, not web)
     */
    const shouldHaveExtraPaddingAtBottomToAccountForIosHomeIndicator = !platform.web && hasHomeIndicator();

    const rnElementsTheme: Theme = {
      colors: {
        primary: nativeBaseTheme.colors.primary[500] || nativeBaseTheme.colors.primary["500"] || "#000000",
        // NOTE: `searchBg` is used as a lighter version of the primary colour when needed in the app
        searchBg: addOpacityToBaseHexColour(
          nativeBaseTheme.colors.primary[500] || nativeBaseTheme.colors.primary["500"] || "#000000",
          12.5
        ),
      },
    };

    const features = createFeaturesObject(user);

    return (
      <NativeBaseProvider theme={nativeBaseTheme}>
        {/* @ts-expect-error - react-native-elements v3.4.2 has incorrect TypeScript definitions for children prop */}
        <ThemeProvider theme={rnElementsTheme}>
          <ActionSheetProvider>
            <FlagsProvider features={features}>
              <SentryReact.ErrorBoundary
                fallback={({ error, resetError }) => (
                  <ErrorBoundaryFallbackComponent error={error} resetError={resetError} t={t} dispatch={dispatch} />
                )}
              >
                {/* Use regular View for web PWA, SafeAreaView for native */}
                {platform.web ? (
                  <View style={styles.webContainer}>
                    <NavigationStack />
                    <StatusBar style="auto" />
                  </View>
                ) : (
                  <SafeAreaView
                    style={[
                      styles.container,
                      // Apply paddingBottom conditionally for native iOS only
                      shouldHaveExtraPaddingAtBottomToAccountForIosHomeIndicator ? { paddingBottom: 30 } : null,
                    ]}
                  >
                    <NavigationStack />
                    <StatusBar style="auto" />
                  </SafeAreaView>
                )}
              </SentryReact.ErrorBoundary>
            </FlagsProvider>
          </ActionSheetProvider>
        </ThemeProvider>
      </NativeBaseProvider>
    );
  };

  // NOTE: Exposing the store for Cypress and Playwright tests
  if ("Cypress" in window || "__PLAYWRIGHT__" in window) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.store = store;
  }

  return (
    <SSRProvider>
      {/* NOTE: Since updating to Expo SDK 48 the type compiler gives an error on this. */}
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore */}
      <Provider store={store}>
        <PersistGate loading={<AppLoadingScreen />} persistor={persistStore(store)}>
          <NativeBaseApp />
        </PersistGate>
      </Provider>
    </SSRProvider>
  );
}

export default App;
